import { FaInfoCircle, FaShieldAlt } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import CustomButton from '../../../components/CustomButton';
import CustomInput from '../../../components/CustomInput';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Alert,
  addToast,
} from '@heroui/react';
import { QRCodeSVG } from 'qrcode.react';
import {
  validateVerificationCode,
  getMfaStatus,
  associateMfa,
  verifyMfa,
  disableMfa,
  generateMfaRecoveryCodes,
} from '../../../../service/twoFactorAuthService';
import { all_routes } from '../../../../core/data/routes/all_routes';
import { useAuth } from '../../../../context/AuthContext';
import AccountDelete from './AccountDelete';
import AccountDeactivate from './AccountDeactivate';
import ReactivateAccount from './ReactivateAccount';
import PasswordChangeSection from './PasswordChangeSection';

const SHOW_TWO_FACTOR_AUTH = false;

const SecuritySettings = () => {
  const { user, customerAccountStatus, isAuthenticated } = useAuth();

  // Two-Factor Authentication modal
  const {
    isOpen: isTwoFactorModalOpen,
    onOpen: onTwoFactorModalOpen,
    onClose: onTwoFactorModalClose,
  } = useDisclosure();

  // Disable 2FA confirmation modal (replace browser confirm())
  const {
    isOpen: isDisableTwoFactorConfirmOpen,
    onOpen: onDisableTwoFactorConfirmOpen,
    onClose: onDisableTwoFactorConfirmClose,
  } = useDisclosure();

  const [_lastLoginTime, setLastLoginTime] = useState<string>('');

  // Two-Factor Authentication state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean>(false);
  const [twoFactorSetupStep, setTwoFactorSetupStep] = useState<
    'intro' | 'scanCode' | 'verify'
  >('intro');
  const [twoFactorQrCode, setTwoFactorQrCode] = useState<string>('');
  const [twoFactorSecret, setTwoFactorSecret] = useState<string>('');
  const [twoFactorSetupSession, setTwoFactorSetupSession] =
    useState<string>('');
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] =
    useState<string>('');
  const [isTwoFactorLoading, setIsTwoFactorLoading] = useState<boolean>(false);
  const [twoFactorAlertMessage, setTwoFactorAlertMessage] =
    useState<string>('');
  const [twoFactorAlertType, setTwoFactorAlertType] = useState<
    'success' | 'error'
  >('success');
  const [showTwoFactorAlert, setShowTwoFactorAlert] = useState<boolean>(false);

  const [disableTwoFactorLoading, setDisableTwoFactorLoading] =
    useState<boolean>(false);
  const {
    isOpen: isRecoveryCodesModalOpen,
    onOpen: onRecoveryCodesModalOpen,
    onClose: onRecoveryCodesModalClose,
  } = useDisclosure();
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryCodesLoading, setRecoveryCodesLoading] =
    useState<boolean>(false);

  // Device management state - removed unused variables
  // Account activity state - removed unused variables

  // Get user email from auth context when component mounts
  useEffect(() => {
    if (user?.email) {
      // Check if this is a new login
      const currentTime = new Date().toISOString();
      const storedLoginTime = localStorage.getItem('lastLoginTime');

      if (!storedLoginTime) {
        // First login, store the current time
        localStorage.setItem('lastLoginTime', currentTime);
        setLastLoginTime(formatDate(currentTime));
      } else {
        // Use the stored login time
        setLastLoginTime(formatDate(storedLoginTime));
      }

      // Check 2FA status from backend
      getMfaStatus()
        .then((response) => {
          setTwoFactorEnabled(response.data.mfaEnabled);
          console.log('MFA Status:', response.data);
        })
        .catch((error) => {
          console.error('Error checking MFA status:', error);
          // Fallback to localStorage for backward compatibility
          const twoFactorStatus = localStorage.getItem('twoFactorEnabled');
          if (twoFactorStatus === 'true') {
            setTwoFactorEnabled(true);
          }
        });

      // Device and activity data loading removed - unused features
    }
  }, [user]);

  // Function to start 2FA setup process
  const startTwoFactorSetup = async () => {
    setIsTwoFactorLoading(true);
    setShowTwoFactorAlert(false);
    setTwoFactorSetupStep('intro');
    setTwoFactorVerificationCode('');
    setTwoFactorSetupSession('');

    try {
      const response = await associateMfa();
      if (response.success) {
        setTwoFactorQrCode(response.data.qrCodeUrl);
        setTwoFactorSecret(response.data.secret);
        setTwoFactorSetupSession(response.data.session || '');
        // We can stay on intro or go straight to scanCode. Let's start with intro to explain.
        setTwoFactorSetupStep('intro');
      } else {
        setTwoFactorAlertMessage(
          response.message || 'Failed to initialize MFA setup'
        );
        setTwoFactorAlertType('error');
        setShowTwoFactorAlert(true);
      }
    } catch (error) {
      console.error('Error starting 2FA setup:', error);
      setTwoFactorAlertMessage(
        'Failed to initialize MFA setup. Please try again.'
      );
      setTwoFactorAlertType('error');
      setShowTwoFactorAlert(true);
    } finally {
      setIsTwoFactorLoading(false);
      onTwoFactorModalOpen();
    }
  };

  // Function to verify 2FA setup with verification code
  const verifyTwoFactorSetup = async () => {
    if (
      !twoFactorVerificationCode ||
      !validateVerificationCode(twoFactorVerificationCode)
    ) {
      setTwoFactorAlertMessage(
        'Please enter a valid 6-digit verification code'
      );
      setTwoFactorAlertType('error');
      setShowTwoFactorAlert(true);
      return;
    }

    setIsTwoFactorLoading(true);
    setShowTwoFactorAlert(false);

    try {
      const response = await verifyMfa(
        twoFactorVerificationCode,
        twoFactorSetupSession || undefined
      );

      if (response.success) {
        // Success - 2FA is now enabled
        setTwoFactorEnabled(true);
        // localStorage.setItem('twoFactorEnabled', 'true'); // Rely on backend or update context if needed

        setTwoFactorAlertMessage(
          response.message || 'Two-Factor Authentication enabled successfully'
        );
        setTwoFactorAlertType('success');
        setShowTwoFactorAlert(true);

        // Close modal after a delay
        setTimeout(() => {
          onTwoFactorModalClose();
          setShowTwoFactorAlert(false);
          setTwoFactorSetupStep('intro');
          setTwoFactorVerificationCode('');
          setTwoFactorSetupSession('');
        }, 2000);
      } else {
        setTwoFactorAlertMessage(response.message || 'Verification failed');
        setTwoFactorAlertType('error');
        setShowTwoFactorAlert(true);
      }
    } catch (error: any) {
      console.error('Error verifying 2FA setup:', error);
      setTwoFactorAlertMessage(
        error.message || 'Failed to verify Two-Factor Authentication setup'
      );
      setTwoFactorAlertType('error');
      setShowTwoFactorAlert(true);
    } finally {
      setIsTwoFactorLoading(false);
    }
  };

  // Function to disable 2FA
  const confirmDisableTwoFactor = async () => {
    setDisableTwoFactorLoading(true);
    setShowTwoFactorAlert(false);

    try {
      const response = await disableMfa();

      if (response.success) {
        setTwoFactorEnabled(false);
        setTwoFactorAlertMessage(response.message);
        setTwoFactorAlertType('success');
        setShowTwoFactorAlert(true);
        setTimeout(() => {
          onDisableTwoFactorConfirmClose();
          setDisableTwoFactorLoading(false);
          setShowTwoFactorAlert(false);
        }, 1000);
      } else {
        setTwoFactorAlertMessage(response.message);
        setTwoFactorAlertType('error');
        setShowTwoFactorAlert(true);
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      setTwoFactorAlertMessage('Failed to disable Two-Factor Authentication');
      setTwoFactorAlertType('error');
      setShowTwoFactorAlert(true);
    } finally {
      setDisableTwoFactorLoading(false);
    }
  };

  const downloadRecoveryCodesAsText = (codes: string[]) => {
    const timestamp = new Date().toISOString();
    const body = [
      'HirePanther MFA Recovery Codes',
      '----------------------------------------',
      'Each code works only once.',
      'Keep this file secure. Anyone with these codes can recover your account.',
      `Generated at: ${timestamp}`,
      '',
      ...codes.map((code, idx) => `${idx + 1}. ${code}`),
      '',
    ].join('\n');

    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const filename = `hirepanther-recovery-codes-${timestamp.slice(0, 10)}.txt`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateRecoveryCodes = async () => {
    setRecoveryCodesLoading(true);
    try {
      const response = await generateMfaRecoveryCodes();
      const codes = response?.data?.codes || [];
      if (!response?.success || codes.length === 0) {
        throw new Error(
          response?.message || 'Failed to generate recovery codes'
        );
      }

      setRecoveryCodes(codes);
      onRecoveryCodesModalOpen();
      setTwoFactorAlertType('success');
      setTwoFactorAlertMessage(
        response.message || 'Recovery codes generated successfully.'
      );
      setShowTwoFactorAlert(true);
      addToast({
        title: 'Recovery codes generated',
        description:
          'Save these codes now. They are shown only once and each code is one-time use.',
        color: 'success',
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to generate recovery codes.';
      addToast({
        title: 'Unable to generate codes',
        description: message,
        color: 'danger',
      });
    } finally {
      setRecoveryCodesLoading(false);
    }
  };

  // Device fetching function removed - unused feature

  // Account activity fetching function removed - unused feature

  // Format date to a readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="bg-white border border-[#dfdeded5] rounded-2xl overflow-hidden">
      <div className="p-5 sm:p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-1">Security</h3>
        <p className="text-xs text-gray-500 mb-4">
          Manage your security settings
        </p>

        <div className="divide-y divide-gray-100">
          {/* Password Change — clickable row with modal trigger */}
          <div className="py-3">
            <PasswordChangeSection />
          </div>

          {/* Two-Factor Authentication */}
          {SHOW_TWO_FACTOR_AUTH && (
            <div className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <FaShieldAlt
                  className="text-[#317690] mt-0.5 flex-shrink-0"
                  size={14}
                />
                <div>
                  <h4 className="text-sm font-medium text-gray-800">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {twoFactorEnabled
                      ? 'Enabled — your account is protected'
                      : 'Add an extra layer of security to your account'}
                  </p>
                  {twoFactorEnabled && (
                    <div className="mt-2 space-y-2">
                      <Link
                        to={all_routes.recoverTwoFactor}
                        className="text-xs text-[#317690] hover:underline inline-block"
                      >
                        Lost your authenticator? Recover account
                      </Link>
                      <div>
                        <CustomButton
                          color="warning"
                          label="Generate Recovery Codes"
                          variant="flat"
                          size="sm"
                          onPress={handleGenerateRecoveryCodes}
                          isLoading={recoveryCodesLoading}
                          isDisabled={recoveryCodesLoading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <CustomButton
                color={twoFactorEnabled ? 'danger' : 'primary'}
                label={twoFactorEnabled ? 'Disable' : 'Enable'}
                variant="flat"
                size="sm"
                className="flex-shrink-0 sm:self-start"
                onPress={
                  twoFactorEnabled
                    ? onDisableTwoFactorConfirmOpen
                    : startTwoFactorSetup
                }
                isLoading={isTwoFactorLoading}
                isDisabled={isTwoFactorLoading}
              />
            </div>
          )}

          {/* Two-Factor Authentication Setup Modal */}
          <Modal
            isOpen={isTwoFactorModalOpen}
            onClose={() => {
              setTwoFactorSetupStep('intro');
              setTwoFactorVerificationCode('');
              setTwoFactorSetupSession('');
              setShowTwoFactorAlert(false);
              onTwoFactorModalClose();
            }}
            placement="center"
            backdrop="blur"
            size="sm"
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-0.5 py-2 px-3">
                <span className="text-sm font-semibold">
                  {twoFactorSetupStep === 'intro'
                    ? 'Set Up Two-Factor Authentication'
                    : twoFactorSetupStep === 'scanCode'
                      ? 'Scan QR Code'
                      : 'Verify Setup'}
                </span>
              </ModalHeader>
              <ModalBody className="px-3 py-2">
                {showTwoFactorAlert && (
                  <Alert
                    className="mb-2 text-xs"
                    color={
                      twoFactorAlertType === 'success' ? 'success' : 'danger'
                    }
                  >
                    {twoFactorAlertMessage}
                  </Alert>
                )}

                {/* Step indicator */}
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={`flex flex-col items-center ${twoFactorSetupStep === 'intro' ? 'text-primary-500' : 'text-gray-400'}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center mb-0.5 text-xs ${twoFactorSetupStep === 'intro' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
                    >
                      1
                    </div>
                    <span className="text-[10px]">Info</span>
                  </div>
                  <div
                    className={`h-0.5 flex-1 mx-1.5 ${twoFactorSetupStep === 'intro' ? 'bg-gray-200' : 'bg-primary-500'}`}
                  ></div>
                  <div
                    className={`flex flex-col items-center ${twoFactorSetupStep === 'scanCode' ? 'text-primary-500' : 'text-gray-400'}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center mb-0.5 text-xs ${twoFactorSetupStep === 'scanCode' ? 'bg-primary-500 text-white' : twoFactorSetupStep === 'verify' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
                    >
                      2
                    </div>
                    <span className="text-[10px]">Scan</span>
                  </div>
                  <div
                    className={`h-0.5 flex-1 mx-1.5 ${twoFactorSetupStep === 'verify' ? 'bg-primary-500' : 'bg-gray-200'}`}
                  ></div>
                  <div
                    className={`flex flex-col items-center ${twoFactorSetupStep === 'verify' ? 'text-primary-500' : 'text-gray-400'}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center mb-0.5 text-xs ${twoFactorSetupStep === 'verify' ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}
                    >
                      3
                    </div>
                    <span className="text-[10px]">Verify</span>
                  </div>
                </div>

                {/* Step content */}
                {twoFactorSetupStep === 'intro' && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold">
                      Enhance Your Account Security
                    </h3>
                    <p className="text-xs text-gray-600">
                      Two-factor authentication (2FA) adds an extra layer of
                      security. You will need an Authenticator App (like Google
                      Authenticator or Authy) to generate verification codes.
                    </p>

                    <div className="bg-blue-50 p-2.5 rounded-md border border-blue-200">
                      <h4 className="text-xs font-medium flex items-center gap-1.5 text-blue-700">
                        <FaInfoCircle /> How it works
                      </h4>
                      <ul className="list-disc pl-4 mt-1 text-xs text-blue-700 space-y-0.5">
                        <li>Download an Authenticator App on your phone</li>
                        <li>Scan the QR code shown in the next step</li>
                        <li>Enter the 6-digit code from the app to verify</li>
                      </ul>
                    </div>
                  </div>
                )}

                {twoFactorSetupStep === 'scanCode' && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold">Scan QR Code</h3>
                    <p className="text-xs text-gray-600">
                      Open your Authenticator App and scan the QR code below.
                    </p>

                    <div className="flex flex-col items-center justify-center space-y-2">
                      {twoFactorQrCode ? (
                        <div className="bg-white p-3 rounded border shadow-sm">
                          <QRCodeSVG
                            value={twoFactorQrCode}
                            size={160}
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                      ) : (
                        <div className="w-[140px] h-[140px] bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                          QR Code Loading...
                        </div>
                      )}

                      <div className="text-center w-full">
                        <p className="text-[10px] text-gray-500 mb-0.5">
                          Unable to scan?
                        </p>
                        <p className="text-xs font-mono bg-gray-100 p-1.5 rounded break-all select-all">
                          {twoFactorSecret}
                        </p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-2.5 rounded-md border border-yellow-200">
                      <h4 className="text-xs font-medium flex items-center gap-1.5 text-yellow-700">
                        <FaInfoCircle /> Important
                      </h4>
                      <p className="text-xs text-yellow-700 mt-0.5">
                        If you cannot scan the QR code, manually enter the
                        secret key above into your authenticator app.
                      </p>
                    </div>
                  </div>
                )}

                {twoFactorSetupStep === 'verify' && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold">Verify Setup</h3>
                    <p className="text-xs text-gray-600">
                      Enter the 6-digit verification code from your
                      Authenticator App.
                    </p>

                    <div className="space-y-2">
                      <CustomInput
                        label="Verification Code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={twoFactorVerificationCode}
                        onValueChange={setTwoFactorVerificationCode}
                        isRequired
                        description="Enter the code shown in your app"
                        maxLength={6}
                      />
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="px-3 py-2">
                {twoFactorSetupStep === 'intro' ? (
                  <>
                    <CustomButton
                      color="danger"
                      variant="flat"
                      size="sm"
                      label="Cancel"
                      onPress={onTwoFactorModalClose}
                    />
                    <CustomButton
                      color="primary"
                      size="sm"
                      label="Next"
                      onPress={() => setTwoFactorSetupStep('scanCode')}
                    />
                  </>
                ) : twoFactorSetupStep === 'scanCode' ? (
                  <>
                    <CustomButton
                      color="danger"
                      variant="flat"
                      size="sm"
                      label="Back"
                      onPress={() => setTwoFactorSetupStep('intro')}
                    />
                    <CustomButton
                      color="primary"
                      size="sm"
                      label="Next"
                      onPress={() => setTwoFactorSetupStep('verify')}
                    />
                  </>
                ) : (
                  <>
                    <CustomButton
                      color="danger"
                      variant="flat"
                      size="sm"
                      label="Back"
                      onPress={() => setTwoFactorSetupStep('scanCode')}
                    />
                    <CustomButton
                      color="primary"
                      size="sm"
                      label="Verify & Enable"
                      onPress={verifyTwoFactorSetup}
                      isLoading={isTwoFactorLoading}
                      isDisabled={
                        !twoFactorVerificationCode ||
                        twoFactorVerificationCode.length < 6
                      }
                    />
                  </>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Modal
            isOpen={isRecoveryCodesModalOpen}
            onClose={onRecoveryCodesModalClose}
            placement="center"
            backdrop="blur"
            size="sm"
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1 py-2">
                <h3 className="text-sm font-semibold">Recovery Codes</h3>
                <p className="text-xs text-gray-600">
                  Save these codes now. Each code can be used only once.
                </p>
              </ModalHeader>
              <ModalBody className="px-3 py-2">
                <Alert className="mb-2 text-xs" color="warning">
                  Keep these codes secure. Anyone with these codes can recover
                  access to your account.
                </Alert>
                <div className="max-h-56 overflow-auto rounded-md border border-default-200 p-2">
                  {recoveryCodes.map((code, idx) => (
                    <div
                      key={`${code}-${idx}`}
                      className="font-mono text-xs py-1"
                    >
                      {idx + 1}. {code}
                    </div>
                  ))}
                </div>
              </ModalBody>
              <ModalFooter className="px-3 py-2">
                <CustomButton
                  color="default"
                  variant="flat"
                  label="Close"
                  onPress={onRecoveryCodesModalClose}
                />
                <CustomButton
                  color="primary"
                  variant="flat"
                  label="Copy"
                  onPress={async () => {
                    await navigator.clipboard.writeText(
                      recoveryCodes.join('\n')
                    );
                    addToast({
                      title: 'Copied',
                      description: 'Recovery codes copied to clipboard.',
                      color: 'success',
                    });
                  }}
                />
                <CustomButton
                  color="primary"
                  variant="solid"
                  label="Download .txt"
                  onPress={() => downloadRecoveryCodesAsText(recoveryCodes)}
                />
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Disable Two-Factor Confirmation Modal */}
          <Modal
            isOpen={isDisableTwoFactorConfirmOpen}
            onClose={() => {
              setDisableTwoFactorLoading(false);
              setShowTwoFactorAlert(false);
              onDisableTwoFactorConfirmClose();
            }}
            placement="center"
            backdrop="blur"
            size="sm"
            isDismissable={false}
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1 py-2">
                <h3 className="text-sm font-semibold">
                  Disable Two-Factor Authentication?
                </h3>
              </ModalHeader>
              <ModalBody className="px-3 py-2">
                <p className="text-xs text-gray-600">
                  Disabling MFA reduces account security. You can enable it
                  again later from this page.
                </p>

                {showTwoFactorAlert && (
                  <Alert
                    className="mt-3 mb-0 text-xs"
                    color={
                      twoFactorAlertType === 'success' ? 'success' : 'danger'
                    }
                  >
                    {twoFactorAlertMessage}
                  </Alert>
                )}
              </ModalBody>
              <ModalFooter className="px-3 py-2">
                <CustomButton
                  color="default"
                  variant="flat"
                  label="Cancel"
                  onPress={() => onDisableTwoFactorConfirmClose()}
                  isDisabled={disableTwoFactorLoading}
                />
                <CustomButton
                  color="danger"
                  variant="solid"
                  label="Disable"
                  onPress={confirmDisableTwoFactor}
                  isLoading={disableTwoFactorLoading}
                  isDisabled={disableTwoFactorLoading}
                />
              </ModalFooter>
            </ModalContent>
          </Modal>
        </div>

        {/* Account status-based actions */}
        {isAuthenticated && (
          <>
            {customerAccountStatus === 'deactivated' && (
              <div className="py-3">
                <ReactivateAccount />
              </div>
            )}
            {customerAccountStatus === 'active' && (
              <div className="py-3">
                <AccountDeactivate />
              </div>
            )}
            {(customerAccountStatus === 'active' ||
              customerAccountStatus === 'deactivated') && (
              <div className="py-3">
                <AccountDelete />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
